import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  ConfirmationNumberOutlined as TicketIcon,
  GroupOutlined as CapacityIcon,
  Filter9PlusOutlined as PickIcon,
  // PercentOutlined as PercentIcon,
  InfoOutlined as InfoIcon,
  AccountBalanceWalletOutlined as FeeIcon,
  HandshakeOutlined as PartnerIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";

const LotterySpecsCard = ({
  lottery,
  entryFeeInCurrency,
  currency,
}) => {
  const isMegaBet = lottery?.lotteryType === "2";
  // const hasRollover = lottery?.lotteryType !== "1" && Boolean(lottery?.rolloverperct);
  const hasPartnerShare = Boolean(lottery?.partnershare);

  return (
    <Card
      className="lottery-glass-card"
      sx={{
        p: { xs: 2.5, sm: 3 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
        <TicketIcon sx={{ color: colors.primary.main, fontSize: 22 }} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: colors.slate[900],
            fontSize: "1.05rem",
          }}
        >
          Game Specifications
        </Typography>
      </Stack>

      <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
        {/* Entry Fee */}
        <Box className="lottery-spec-row">
          <Stack direction="row" spacing={1} alignItems="center">
            <FeeIcon sx={{ color: colors.amber[600], fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: colors.slate[700], fontWeight: 500 }}>
              Ticket Entry Fee
            </Typography>
            <Tooltip title="Cost required to participate with 1 ticket entry in this lottery draw.">
              <InfoIcon sx={{ color: colors.slate[400], fontSize: 14, cursor: "pointer" }} />
            </Tooltip>
          </Stack>
          <Typography variant="body2" sx={{ fontWeight: 700, color: colors.amber[700] }}>
            {entryFeeInCurrency} {currency}
          </Typography>
        </Box>

        {/* Total Capacity */}
        <Box className="lottery-spec-row">
          <Stack direction="row" spacing={1} alignItems="center">
            <CapacityIcon sx={{ color: colors.primary.main, fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: colors.slate[700], fontWeight: 500 }}>
              Maximum Ticket Capacity
            </Typography>
            <Tooltip title="Total number of ticket slots available in this lottery game.">
              <InfoIcon sx={{ color: colors.slate[400], fontSize: 14, cursor: "pointer" }} />
            </Tooltip>
          </Stack>
          <Typography variant="body2" sx={{ fontWeight: 700, color: colors.slate[900] }}>
            {lottery?.capacity || "Unlimited"} Slots
          </Typography>
        </Box>

        {/* Pick Numbers */}
        <Box className="lottery-spec-row">
          <Stack direction="row" spacing={1} alignItems="center">
            <PickIcon sx={{ color: colors.purple[600], fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: colors.slate[700], fontWeight: 500 }}>
              Required Pick Numbers
            </Typography>
            <Tooltip title="The quantity of numbers a player selects per ticket.">
              <InfoIcon sx={{ color: colors.slate[400], fontSize: 14, cursor: "pointer" }} />
            </Tooltip>
          </Stack>
          <Typography variant="body2" sx={{ fontWeight: 700, color: colors.slate[900] }}>
            {lottery?.pickNumbers || "1"} {Number(lottery?.pickNumbers) === 1 ? "Number" : "Numbers"}
          </Typography>
        </Box>

        {/* Minimum Players (for MegaBET) */}
        {isMegaBet && (
          <Box className="lottery-spec-row">
            <Stack direction="row" spacing={1} alignItems="center">
              <CapacityIcon sx={{ color: colors.emerald[600], fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: colors.slate[700], fontWeight: 500 }}>
                Minimum Required Players
              </Typography>
              <Tooltip title="Minimum participant count required for draw execution without auto-rollover.">
                <InfoIcon sx={{ color: colors.slate[400], fontSize: 14, cursor: "pointer" }} />
              </Tooltip>
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 700, color: colors.emerald[600] }}>
              {lottery?.minPlayers || 2} Players
            </Typography>
          </Box>
        )}

        {/* Rollover Percentage - COMMENTED OUT */}
        {/* {hasRollover && (
          <Box className="lottery-spec-row">
            <Stack direction="row" spacing={1} alignItems="center">
              <PercentIcon sx={{ color: colors.cyan[600], fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: colors.slate[700], fontWeight: 500 }}>
                Rollover Prize Percentage
              </Typography>
              <Tooltip title="Percentage of entry fees accumulated into the rollover jackpot if no winning match is drawn.">
                <InfoIcon sx={{ color: colors.slate[400], fontSize: 14, cursor: "pointer" }} />
              </Tooltip>
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 700, color: colors.cyan[700] }}>
              {lottery?.rolloverperct}%
            </Typography>
          </Box>
        )} */}

        {/* Partner Share */}
        {hasPartnerShare && (
          <Box className="lottery-spec-row">
            <Stack direction="row" spacing={1} alignItems="center">
              <PartnerIcon sx={{ color: colors.purple[600], fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: colors.slate[700], fontWeight: 500 }}>
                Partner Co-host Share
              </Typography>
              <Tooltip title="Share allocated to the co-hosting partner organization.">
                <InfoIcon sx={{ color: colors.slate[400], fontSize: 14, cursor: "pointer" }} />
              </Tooltip>
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 700, color: colors.purple[700] }}>
              {lottery?.partnershare}%
            </Typography>
          </Box>
        )}
      </Stack>
    </Card>
  );
};

LotterySpecsCard.propTypes = {
  lottery: PropTypes.object,
  entryFeeInCurrency: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
};

export default LotterySpecsCard;
